/**
 * Multi-currency display for ForgeLink.
 * Catalog unit prices are stored in USD; NGN tiers (when present) are converted via demo FX.
 *
 * Demo FX rates — units of each currency per 1 USD (not live market rates):
 *   USD 1.00
 *   NGN 1600   (Nigeria default)
 *   GBP 0.79   (British pounds — user said "bounds")
 *   CNY 7.25   (Chinese yuan)
 */
const STORAGE_KEY = 'forgelink_currency';
const DEFAULT_CODE = 'NGN';

export const FX_PER_USD = Object.freeze({
  USD: 1,
  NGN: 1600,
  GBP: 0.79,
  CNY: 7.25,
});

/** @deprecated Prefer FX_PER_USD.NGN — kept for existing imports. */
export const USD_TO_NGN = FX_PER_USD.NGN;

export const CURRENCIES = Object.freeze({
  NGN: {
    code: 'NGN',
    label: '₦ NGN',
    locale: 'en-NG',
    fractionDigits: 0,
  },
  USD: {
    code: 'USD',
    label: '$ USD',
    locale: 'en-US',
    fractionDigits: 2,
  },
  GBP: {
    code: 'GBP',
    label: '£ GBP',
    locale: 'en-GB',
    fractionDigits: 2,
  },
  CNY: {
    code: 'CNY',
    label: '¥ CNY',
    locale: 'zh-CN',
    fractionDigits: 2,
  },
});

export const CURRENCY_CODES = Object.freeze(Object.keys(CURRENCIES));

function normalizeCode(code) {
  const upper = String(code || '').toUpperCase();
  return CURRENCIES[upper] ? upper : DEFAULT_CODE;
}

export function getCurrency() {
  try {
    return normalizeCode(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_CODE;
  }
}

export function setCurrency(code) {
  const next = normalizeCode(code);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function convertFromUsd(usd, toCode = getCurrency()) {
  const rate = FX_PER_USD[normalizeCode(toCode)] ?? 1;
  return (Number(usd) || 0) * rate;
}

export function convertFromNgn(ngn, toCode = getCurrency()) {
  const usd = (Number(ngn) || 0) / FX_PER_USD.NGN;
  return convertFromUsd(usd, toCode);
}

/**
 * Format an amount in the selected (or overridden) display currency.
 * @param {number} n
 * @param {{ from?: 'usd'|'ngn', currency?: string }} [opts]
 */
export function formatMoney(n, opts = {}) {
  const from = opts.from || 'usd';
  const code = normalizeCode(opts.currency || getCurrency());
  const meta = CURRENCIES[code];
  const amount = from === 'ngn' ? convertFromNgn(n, code) : convertFromUsd(n, code);
  const rounded =
    meta.fractionDigits === 0 ? Math.round(amount) : Math.round(amount * 100) / 100;
  return new Intl.NumberFormat(meta.locale, {
    style: 'currency',
    currency: code,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: meta.fractionDigits,
    maximumFractionDigits: meta.fractionDigits,
  }).format(rounded);
}

/** Format an amount that is already denominated in NGN. */
export function formatMoneyNgn(ngn) {
  return formatMoney(ngn, { from: 'ngn' });
}
